import React, { useState, useCallback, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { SegmentedButtons, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { RecipeListParams, Recipe } from '@coffeeplexity/shared';
import { RecipeSearch } from '../components/recipes/RecipeSearch';
import { RecipeList } from '../components/recipes/RecipeList';
import { TrendingRecipes } from '../components/recipes/TrendingRecipes';
import { FeaturedRecipes } from '../components/recipes/FeaturedRecipes';
import { ScrollToTopFAB } from '../components/ui';

export function RecipesScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useState<RecipeListParams>({});
  const [activeView, setActiveView] = useState('all');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const listRef = useRef<any>(null);

  const handleRecipePress = useCallback(
    (recipe: Recipe) => {
      (navigation as any).navigate('recipeDetail', { recipeId: recipe.id });
    },
    [navigation]
  );

  const handleParamsChange = useCallback((params: RecipeListParams) => {
    setSearchParams(params);
    setActiveView('all');
  }, []);

  const handleViewChange = useCallback((view: string) => {
    setActiveView(view);
    if (view === 'trending') {
      setSearchParams({ sort_by: 'view_count', sort_order: 'desc' });
    } else if (view === 'featured') {
      setSearchParams({ filters: { is_featured: true } });
    } else {
      setSearchParams({});
    }
  }, []);

  const handleScrollToTop = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);

  const handleScroll = useCallback((event: any) => {
    const yOffset = event.nativeEvent.contentOffset.y;
    setShowScrollToTop(yOffset > 300);
  }, []);

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header handled by navigator with a right-side New action */}

      {/* View Toggle */}
      <View style={styles.viewToggle}>
        <SegmentedButtons
          value={activeView}
          onValueChange={handleViewChange}
          buttons={[
            { value: 'all', label: 'All' },
            { value: 'trending', label: 'Trending' },
            { value: 'featured', label: 'Featured' },
          ]}
        />
      </View>

      {/* Search */}
      {activeView === 'all' && (
        <View style={styles.searchContainer}>
          <RecipeSearch
            onParamsChange={handleParamsChange}
            initialParams={searchParams}
          />
        </View>
      )}

      {/* Content */}
      <View style={styles.content}>
        {activeView === 'all' && (
          <RecipeList
            ref={listRef}
            params={searchParams}
            onRecipePress={handleRecipePress}
            onScroll={handleScroll}
          />
        )}

        {activeView === 'trending' && (
          <TrendingRecipes
            ref={listRef}
            limit={20}
            showHeader={false}
            showViewAll={false}
            onRecipePress={handleRecipePress}
            horizontal={false}
            onScroll={handleScroll}
          />
        )}

        {activeView === 'featured' && (
          <FeaturedRecipes
            ref={listRef}
            limit={20}
            showHeader={false}
            showViewAll={false}
            onRecipePress={handleRecipePress}
            variant='grid'
            onScroll={handleScroll}
          />
        )}
      </View>

      {/* Scroll to Top FAB */}
      <ScrollToTopFAB
        visible={showScrollToTop}
        onPress={handleScrollToTop}
        testID='recipes-scroll-to-top'
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  title: {
    fontWeight: 'bold',
  },
  viewToggle: {
    paddingHorizontal: 16,
    marginBottom: 12,
    marginTop: 16,
  },
  searchContainer: {
    marginBottom: 0,
  },
  content: {
    flex: 1,
  },
  quickAccess: {
    flex: 1,
  },
});
